import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';

import amplitude from 'amplitude-js';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Row } from 'apps/auth/components/common/Layout/Row';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import { Text } from 'apps/auth/components/common/Text/Text';
import { getField } from 'apps/auth/components/form-multi/utils';
import ValidationPopup from 'apps/auth/components/ga/activity/popup/ValidationPopup';
import VisaUkrainePopup from 'apps/auth/components/notificationPopup/VisaUkrainePopup';
import UsaHighRiskCountryOptInModal from 'apps/auth/components/send/optIn/UsaHighRiskCountryOptInModal';
import TextPopup from 'apps/auth/components/sharedComponents/popups/TextPopup';
import { getRecipientTypeParam, providerCategories } from 'apps/auth/components/utils/AmplitudeUtils';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import ModalNew from 'apps/auth/components/utils/ModalNew';
import { RECIPIENT_SELECTED, RECIPIENTS_LIST_SHOWN, RESTRICTED_COUNTRY_BOTTOM_SHEET_SHOWN } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { PAYMENT_FLOW, PAYOUT_COUNTRY, PAYOUT_METHOD, RECIPIENT_TYPE } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { RECIPIENT } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import {
    AUTH,
    AUTH_BONUS_WITHDRAW,
    AUTH_CHECK_AVAILABLE_DELIVERY_METHOD,
    AUTH_GA_POCKET,
    AUTH_SEND_LINK,
    AUTH_SEND_REPEAT,
    SEND,
} from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { getToAccount } from 'apps/auth/helpers/historyUtils';
import { resolveArguments, toLowerCase } from 'apps/auth/helpers/utils';
import { useExtra, usePageMessages } from 'apps/auth/hooks/selectors';

import { PanelButton } from '../components/PanelButton/PanelButton';
import RecipientListItem from '../List/RecipientListItem';

const RecipientList = (props) => {
    const messages = usePageMessages();
    const popupRef = useRef(null);
    const popupFullwidthRef = useRef(null);
    const modalRef = useRef(null);
    const navigate = useNavigate();
    const { sendFlowV3 } = useExtra();

    useEffect(() => {
        amplitude.getInstance().logEvent(RECIPIENTS_LIST_SHOWN);
    }, []);
    const controllerRef = useRef(null);

    const getTitle = (recipient = {}) => {
        if (recipient.comment) {
            return recipient.comment;
        }
        return recipient?.extra?.to_recipient;
    };

    const getDisabledCountryCheckHref = (recipient) => {
        const countryCode = toLowerCase(recipient?.toCountry?.code);
        const recipientType = props.recipientType || recipient?.extra?.to_recipient_category_type;
        const sendUriPostfix = recipientType ? `?type=${recipientType}` : '';
        if (props.pocketId) {
            return `${AUTH_GA_POCKET}${props.pocketId}/send/${countryCode}/delivery${sendUriPostfix}`;
        }
        return `${props.isBonus ? AUTH_BONUS_WITHDRAW : AUTH_SEND_LINK}/${countryCode}/delivery${sendUriPostfix}`;
    };

    const openDisabledRecipientPopup = (recipient, toCountryName) => {
        const args = [messages.notificationCountryName, toCountryName];
        amplitude.getInstance().logEvent(RESTRICTED_COUNTRY_BOTTOM_SHEET_SHOWN, {
            [PAYOUT_COUNTRY]: recipient?.toCountry?.nameEn,
        });
        popupRef.current?.open(<TextPopup popupRef={popupRef} text={resolveArguments(messages.notificationText, args)} buttonText={messages.gotIt} />);
    };

    const checkDisabledRecipientCountry = (recipient, toCountryName) => {
        if (!recipient?.toCountry?.code) {
            openDisabledRecipientPopup(recipient, toCountryName);
            return;
        }
        props.toggleSpinner(true);
        apiFetchData(getDisabledCountryCheckHref(recipient))
            .then((json) => {
                const updatedCountry = json?.extra?.toCountry || recipient?.toCountry;
                if (updatedCountry?.migrationOnly) {
                    if (json?.messages) {
                        props.updatePageMessages(json.messages);
                    }
                    popupFullwidthRef.current?.open(
                        <UsaHighRiskCountryOptInModal
                            modalRef={modalRef}
                            popupFullwidthRef={popupFullwidthRef}
                            country={updatedCountry}
                            title={updatedCountry?.migrationOnlyTitle}
                            subtitle={updatedCountry?.migrationOnlySubTitle}
                        />,
                    );
                    return;
                }
                openDisabledRecipientPopup(recipient, toCountryName);
            })
            .catch(() => {
                openDisabledRecipientPopup(recipient, toCountryName);
            })
            .finally(() => {
                props.toggleSpinner(false);
            });
    };

    const recipientClickHandler = (e, recipient, href, isDisabled, isBonus, toCountryName, providerToCategory) => {
        e.preventDefault();
        if (isDisabled) {
            checkDisabledRecipientCountry(recipient, toCountryName);
        } else {
            props.toggleSpinner(true);
            apiFetchData(
                isBonus ? `${AUTH}${SEND}/bonusWithdrawLimit` : AUTH_CHECK_AVAILABLE_DELIVERY_METHOD,
                {
                    fromCurrencyIso: getField('fromCurrencyIso', recipient.fromCurrencyIso),
                    pocketId: getField('pocketId', props.pocketId),
                    toCurrencyIso: getField('toCurrencyIso', recipient.toCurrencyIso),
                    globalToCnrId: getField('globalToCnrId', recipient?.extra?.['__sys_pay_global_to_cnr_id']),
                    lastPaymentId: getField('lastPaymentId', recipient?.lastPaymentId),
                    recipientId: getField('recipientId', recipient?.id),
                    lastPaymentDateUtc: getField('lastPaymentDateUtc', recipient?.lastPaymentDateUtc),
                    toCntId: getField('toCntId', recipient.toCntId),
                    toProviderCategoryId: getField('toProviderCategoryId', recipient.toProviderCategory),
                },
                controllerRef,
            )
                .then((json) => {
                    if (json.extra?.visaUkraine) {
                        const redirectUrl = props.pocketId
                            ? `${AUTH_GA_POCKET}${props.pocketId}/send/gt/repeat/${recipient?.lastPaymentId}`
                            : `${AUTH_SEND_REPEAT}?repeatId=${recipient?.lastPaymentId}&fromSend=true`;
                        const changeDeliveryMethod = props.pocketId
                            ? `${AUTH_GA_POCKET}${props.pocketId}/send/${recipient?.toCountry.code}/${toLowerCase(recipient.toCurrencyIso)}`
                            : `${AUTH_SEND_LINK}/${recipient?.toCountry.code}/${toLowerCase(recipient.toCurrencyIso)}`;
                        props.toggleSpinner(false);
                        popupRef.current?.open(
                            <VisaUkrainePopup
                                popupRef={popupRef}
                                text={json.messages?.visaUkraineNotificationText}
                                continueButtonUrl={redirectUrl}
                                continueButtonText={json.messages?.continueButton}
                                changeButtonText={json.messages?.changeDeliveryMethod}
                                changeButtonUrl={changeDeliveryMethod}
                            />,
                        );
                    } else if (json.extra?.showBonusRestriction) {
                        props.toggleSpinner(false);
                        popupRef.current?.open(<TextPopup popupRef={popupRef} text={messages.insufficientBonusFunds} buttonText={messages.gotIt} />);
                    } else if (json?.extra?.invalidBalance) {
                        props.toggleSpinner(false);
                        popupRef.current?.open(<ValidationPopup data={json} pocketId={props.pocketId} popupRef={popupRef} />);
                    } else if (!json.extra?.deliveryMethodAvailable) {
                        props.toggleSpinner(false);
                        popupRef.current?.open(<TextPopup popupRef={popupRef} text={messages.transferMethodNotAvailable} buttonText={messages.gotIt} />);
                    } else if (json.extra?.transferTemporarilyUnavailableSourceTo) {
                        props.toggleSpinner(false);
                        popupRef.current?.open(
                            <TextPopup
                                popupRef={popupRef}
                                text={messages.transferTemporarilyNotAvailableSource}
                                buttonText={messages.changeDeliveryOption}
                                onClick={() => {
                                    popupRef.current?.close();
                                    navigate(`${AUTH_SEND_LINK}/${recipient?.toCountry.code}/delivery`);
                                }}
                            />,
                        );
                    } else {
                        const deliveryMethod = providerToCategory.toString();
                        const amplitudeDeliveryMethod = providerCategories[deliveryMethod];
                        const recipientType = getRecipientTypeParam(recipient?.extra?.to_recipient_category_type);
                        amplitude.getInstance().logEvent(RECIPIENT_SELECTED, {
                            [PAYOUT_COUNTRY]: recipient?.toCountry?.nameEn,
                            [PAYOUT_METHOD]: amplitudeDeliveryMethod,
                            [PAYMENT_FLOW]: RECIPIENT,
                            [RECIPIENT_TYPE]: recipientType,
                        });
                        window.location.href = href;
                    }
                })
                .catch(() => {
                    props.toggleSpinner(false);
                    popupRef.current?.open(<TextPopup popupRef={popupRef} text={messages.transferMethodNotAvailable} buttonText={messages.gotIt} />);
                });
        }
    };

    return (
        <>
            <ModalComponent isIconClose linkTo={props.linkTo}>
                <HeaderPanel title={props.title} subtitle={props.subtitle} />
                <Row indent="adaptive-24-32">
                    <PanelButton
                        icon="person"
                        mainText={sendFlowV3 ? messages.newRecipientDetails : messages.newRecipient}
                        onClick={(e) => {
                            e.preventDefault();
                            props.toggleRecipientList(false);
                        }}
                        dataTestId="new-recipient-button"
                    />
                </Row>
                <Text variant="div" color="secondary" size="subheader" indent="adaptive-16-24">
                    {messages.myRecipients}
                </Text>
                {props.recipientList.map((r) => (
                    <RecipientListItem
                        key={`recipient_${r.id}`}
                        clickHandler={recipientClickHandler}
                        isBonus={props.isBonus}
                        paymentId={r.lastPaymentId}
                        pocketId={props.pocketId}
                        providerToCategory={r.toProviderCategory}
                        toCountryCode={r?.toCountry?.code}
                        isDisabledCountry={r?.toCountry?.disabled}
                        toCountryName={r?.toCountry?.name}
                        leftTitle={getTitle(r)}
                        leftSecondaryText={getToAccount(r.toProviderCategory, r?.toCountry?.id || r?.toCntId, r.account, messages)}
                        rightTitle={r.toCurrencyIso}
                        recipient={r}
                        popupRef={popupRef}
                        recipientList={props.recipientList}
                    />
                ))}
            </ModalComponent>
            <PopupNew ref={popupRef} />
            <PopupNew ref={popupFullwidthRef} fullwidthPopup />
            <ModalNew ref={modalRef} />
        </>
    );
};

RecipientList.propTypes = {
    toggleRecipientList: PropTypes.func.isRequired,
    pocketId: PropTypes.number,
    toggleSpinner: PropTypes.func.isRequired,
    linkTo: PropTypes.string,
    title: PropTypes.string,
    recipientList: PropTypes.array.isRequired,
    updatePageMessages: PropTypes.func.isRequired,
};

export default connect(null, { updatePageMessages })(RecipientList);
